import React, { useState, useEffect } from 'react';
import { VideoPlayer as VideoPlayerView, VideoPlayerProps as VideoPlayerViewProps } from 'components/views/organisms/VideoPlayer';
import { Channel } from 'core/models/channel';
import { exhaustiveCheck } from 'utility/exhaustiveCheck';
import { useChannelStatus } from 'services/useChannelStatus';
import { useChannelPrepare, useChannelStream } from 'services/useChannelStream';
import { PublishRequest, usePublishRequest } from 'services/usePublishRequest';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';

export type VideoPlaybackState = 'BLANK' | 'LOADING' | 'PLAYING' | 'PAUSING';

export function useIsPlayingState() {
    const [isPlaying, setIsPlaying] = useState<boolean>(false);

    return {
        isPlaying,
        playbackCallbacks: {
            onPlaying: () => {
                setIsPlaying(true);
            },
            onPause: () => {
                setIsPlaying(false);
            },
            onWaiting: () => {
                setIsPlaying(false);
            },
        }
    };
}

export function togglePlayback(state: VideoPlaybackState) {
    switch (state) {
        case 'PLAYING':
        case 'LOADING':
            return PublishRequest.STOP;
        case 'PAUSING':
            return PublishRequest.START;
        case 'BLANK':
            // ignore
            return undefined;
        default:
            console.error(exhaustiveCheck(state));
            return undefined;
    }
}

export type VideoPlayerContainerProps = {
    channel: Channel;
    mirror: boolean;
    upsidedown: boolean;
    audio: boolean;
    viewOnly: boolean;
    projectId: Project.Id;
    environmentId: Environment.Id;
    stretch?: boolean;
    preserveAspectRatio?: boolean;
    onPrepare?: (prepared: boolean) => void;
}

export const VideoPlayer = (props: VideoPlayerContainerProps) => {
    const { channel, mirror, upsidedown, audio, viewOnly, projectId, environmentId, stretch, preserveAspectRatio } = props;
    const { onPrepare } = props;

    const prepared = useChannelPrepare(channel);
    useEffect(() => {
        if (!onPrepare) return;
        onPrepare(prepared);
    }, [onPrepare, prepared]);

    const stream = useChannelStream(channel, audio);
    const isStarted = useChannelStatus(channel);
    const { isPlaying, playbackCallbacks } = useIsPlayingState();
    const [requesting, setRequesting] = usePublishRequest(channel.mediaDeviceId, projectId, environmentId);

    const [state, setState] = useState<VideoPlaybackState>('BLANK');
    useEffect(() => {
        if (isStarted === false) {
            if (requesting === PublishRequest.START) {
                setState('LOADING');
            } else {
                setState('PAUSING');
                setRequesting(undefined);
            }
        } else {
            if (requesting === PublishRequest.STOP) {
                setState('PAUSING');
            } else {
                if (isPlaying) {
                    setState('PLAYING');
                } else {
                    setState('LOADING');
                }
            }
        }
    }, [isPlaying, isStarted, requesting, setRequesting]);

    const _props: VideoPlayerViewProps = {
        stream,
        pause: state == 'PAUSING',
        loading: state == 'LOADING',
        stretch,
        preserveAspectRatio,
        mirror,
        upsidedown,
        onClick: viewOnly
            ? undefined
            : () => setRequesting(togglePlayback(state)),
        ...playbackCallbacks,
    };

    return (
        <VideoPlayerView {..._props} />
    );
};
